import React, { useState } from "react";
import { FiArrowUpRight } from "react-icons/fi";
import SectionHeader from "../SectionHeader/SectionHeader";
import useReveal from "../../useReveal";

const photoPath = "https://portfolio-images-juhani.s3.eu-north-1.amazonaws.com/";

const projects = [
  {
    name: "Frendi — AI Assistant",
    type: "Work",
    image: "/frendi-ai.jpg",
    featured: true,
    imageAlt: "Frendi AI assistant answering an insurance question",
    live: "https://www.fennia.fi",
    description:
      "Fennia's generative-AI assistant for personal customers. Frendi answers insurance questions around the clock using Fennia's public information and guides customers to the right service or next step.",
    tech: ["Salesforce Agentforce", "Data 360", "AWS", "Generative AI"],
  },
  {
    name: "Oma Fennia",
    type: "Work",
    image: "/oma-fennia-grand-one.png",
    featured: true,
    imageAlt: "Oma Fennia digital insurance service shown on a mobile phone",
    live: "https://grandone.fi/kilpailutyo/?entry=oma-fennia-digital-insurance-reimagined",
    description:
      "I served as Dev Lead for the renewal of Fennia's personal-customer portal. The new Oma Fennia brings policies and self-service journeys into one clearer digital experience — recognised as a Grand One 2026 finalist in Best Digital Service.",
    tech: ["Dev Lead", "Salesforce", "Digital Insurance", "Design System"],
  },
  {
    name: "Investment Portfolio",
    type: "For fun",
    image: "/investment-portfolio-demo.png",
    featured: true,
    imageAlt: "Investment Portfolio dashboard with fictional balances",
    live: "",
    description:
      "A private dashboard that combines investments from multiple providers with bank balances and cash-flow data from Enable Banking. The mobile-first Next.js frontend runs on a Cognito-protected AWS backend.",
    tech: ["Next.js", "React", "AWS", "Enable Banking", "Cognito", "DynamoDB"],
  },
  {
    name: "Movies Worldwide",
    type: "For fun",
    image: "/movies-search.jpg",
    featured: true,
    imageAlt: "Worldwide movie availability search showing streaming services by country",
    live: "https://movies.rajalahti.me/",
    description:
      "Searches movie availability across streaming services and countries, then shows where each title can be watched. Cognito-authenticated users can save films to a personal watchlist.",
    tech: ["React", "AWS Lambda", "Cognito", "DynamoDB", "JustWatch"],
  },
  {
    name: "Fennia.fi",
    type: "Work",
    image: photoPath + "fenniaFI.jpg",
    live: "https://www.fennia.fi",
    description:
      "A fresh website for Fennia, a Finnish insurance company. I helped build, improve and maintain the site as part of the team.",
    tech: ["React", "AWS", "GatsbyJS", "Contentful", "NodeJS"],
  },
  {
    name: "Kompassi — Sales UI",
    type: "Work",
    image: photoPath + "kompassi.jpg",
    live: "",
    description:
      "A front-end insurance sales UI for Fennia's account managers, built with Rohea. I've led it since 2015 as project manager and business analyst.",
    tech: ["React", "MongoDB", "PACE platform"],
  },
  {
    name: "Espoo Today",
    type: "For fun",
    image: photoPath + "espoo-today.jpg",
    live: "https://espoo-today.rajalahti.me",
    description:
      "Weather and current-events app for Espoo, stitching together OpenWeatherMap, Helsinki's event API and real-time IoT water-temperature data.",
    tech: ["React", "Material UI", "AWS"],
  },
  {
    name: "Satukone — AI Stories",
    type: "For fun",
    image: photoPath + "fantasy.jpg",
    live: "https://satukone.rajalahti.me",
    description:
      "A story generator powered by GPT-3, translated to Finnish with DeepL and voiced with AWS Polly audio files.",
    tech: ["React", "Material UI", "GPT-3", "DeepL", "AWS Polly"],
  },
  {
    name: "Random Traveller",
    type: "For fun",
    image: photoPath + "random_traveller.jpg",
    live: "http://randomtraveller.surge.sh",
    description:
      "Picks a random spot across Europe, the US, Finland, Thailand or Japan and drops you into Street View. Liked places persist in DynamoDB.",
    tech: ["React", "AWS Lambda", "DynamoDB", "Google Maps", "HERE"],
  },
  {
    name: "Investment Calculator",
    type: "For fun",
    image: photoPath + "investment_calc.jpg",
    live: "http://investmentcalculator.s3-website.eu-north-1.amazonaws.com/",
    description:
      "Index funds and ETFs have been my hobby for a decade. A simple calculator that visualises compounding with Chart.js.",
    tech: ["React", "Chart.js", "AWS Lambda"],
  },
];

function ProjectRow({ project, num, onHover }) {
  const [hover, setHover] = useState(false);
  const clickable = Boolean(project.live);
  const tagColor = project.type === "Work" ? "var(--cyan)" : "var(--amber)";

  const content = (
    <div
      className="project-row-inner"
      style={{
        display: "grid",
        gridTemplateColumns: "auto 1fr auto",
        alignItems: "center",
        gap: "clamp(16px, 4vw, 40px)",
        padding: "clamp(22px, 4vh, 34px) clamp(10px, 2vw, 22px)",
        position: "relative",
        background: hover ? "rgba(52,224,255,0.05)" : "transparent",
        transition: "background 0.3s ease",
      }}
    >
      {/* left status bar that lights up */}
      <span
        className="project-index"
        style={{
          position: "absolute",
          left: 0,
          top: 0,
          bottom: 0,
          width: "2px",
          background: "var(--cyan)",
          boxShadow: "0 0 12px var(--glow)",
          transform: hover ? "scaleY(1)" : "scaleY(0)",
          transformOrigin: "center",
          transition: "transform 0.35s var(--ease)",
        }}
      />
      <span
        className="project-arrow"
        style={{
          fontFamily: "var(--mono)",
          fontSize: "0.82rem",
          color: hover ? "var(--cyan)" : "var(--text-faint)",
          transition: "color 0.3s ease",
        }}
      >
        {num}
      </span>

      <div>
        <div style={{ display: "flex", alignItems: "baseline", gap: "16px", flexWrap: "wrap" }}>
          <h3
            className={hover ? "glow-text" : ""}
            style={{
              fontFamily: "var(--display)",
              fontSize: "clamp(1.5rem, 4.2vw, 3rem)",
              fontWeight: 700,
              lineHeight: 1,
              letterSpacing: "0.01em",
              textTransform: "uppercase",
              color: hover ? "var(--cyan)" : "var(--text)",
              transform: hover ? "translateX(10px)" : "none",
              transition: "color 0.3s ease, transform 0.4s var(--ease)",
            }}
          >
            {project.name}
          </h3>
          <span
            style={{
              fontFamily: "var(--mono)",
              fontSize: "0.64rem",
              textTransform: "uppercase",
              letterSpacing: "0.14em",
              padding: "4px 9px",
              border: `1px solid ${tagColor}`,
              color: tagColor,
            }}
          >
            {project.type === "Work" ? "MISSION" : "SIDE-OP"}
          </span>
        </div>

        <p
          style={{
            marginTop: "14px",
            maxWidth: "64ch",
            color: "var(--text-soft)",
            fontSize: "0.98rem",
            lineHeight: 1.7,
          }}
        >
          {project.description}
        </p>

        {project.image && (
          <div
            className="project-mobile-image"
            style={{
              display: "none",
              marginTop: "20px",
              maxWidth: "520px",
              height: "min(70vw, 340px)",
              overflow: "hidden",
              border: "1px solid var(--line-strong)",
              background: "var(--void-2)",
            }}
          >
            <img
              src={project.image}
              alt={project.imageAlt || `${project.name} project`}
              loading="lazy"
              decoding="async"
              style={{ width: "100%", height: "100%", objectFit: "cover", objectPosition: "center top" }}
            />
          </div>
        )}

        <ul
          style={{
            listStyle: "none",
            display: "flex",
            flexWrap: "wrap",
            gap: "8px",
            marginTop: "16px",
          }}
        >
          {project.tech.map((t) => (
            <li
              key={t}
              style={{
                fontFamily: "var(--mono)",
                fontSize: "0.7rem",
                color: "var(--text-soft)",
                padding: "3px 9px",
                border: "1px solid var(--line)",
                background: "rgba(52,224,255,0.04)",
              }}
            >
              {t}
            </li>
          ))}
        </ul>
      </div>

      <span
        style={{
          fontSize: "1.6rem",
          color: hover ? "var(--cyan)" : "var(--text-faint)",
          transform: hover ? "rotate(0deg) scale(1.2)" : "rotate(-12deg)",
          transition: "all 0.35s var(--ease)",
          opacity: clickable ? 1 : 0.25,
          filter: hover ? "drop-shadow(0 0 8px var(--glow))" : "none",
        }}
      >
        <FiArrowUpRight />
      </span>
    </div>
  );

  const rowStyle = {
    display: "block",
    textDecoration: "none",
    color: "inherit",
    borderTop: "1px solid var(--line)",
    cursor: clickable ? "pointer" : "default",
    position: "relative",
  };

  const handlers = {
    onMouseEnter: () => { setHover(true); onHover(project.image); },
    onMouseLeave: () => { setHover(false); onHover(null); },
  };

  return clickable ? (
    <a
      href={project.live}
      target="_blank"
      rel="noreferrer noopener"
      className="reveal"
      data-stagger="70"
      style={rowStyle}
      {...handlers}
    >
      {content}
    </a>
  ) : (
    <div className="reveal" data-stagger="70" style={rowStyle} {...handlers}>
      {content}
    </div>
  );
}

export default function Projects() {
  const ref = useReveal();
  const [preview, setPreview] = useState(null);

  return (
    <section
      id="work"
      ref={ref}
      style={{
        maxWidth: "var(--maxw)",
        margin: "0 auto",
        padding: "clamp(60px, 10vh, 120px) clamp(20px, 5vw, 48px)",
        position: "relative",
      }}
    >
      <SectionHeader index="02" kicker="mission log" title="Projects" />

      {/* Floating HUD preview that tracks the hovered row (desktop) */}
      <div
        className="project-preview"
        style={{
          position: "fixed",
          top: "50%",
          right: "5vw",
          transform: "translateY(-50%)",
          width: "340px",
          height: "240px",
          pointerEvents: "none",
          zIndex: 5,
          opacity: preview ? 1 : 0,
          transition: "opacity 0.3s ease",
          border: "1px solid var(--cyan)",
          boxShadow: "0 0 30px rgba(52,224,255,0.35)",
          overflow: "hidden",
          background: "var(--void-2)",
        }}
      >
        {preview && (
          <>
            <img
              src={preview}
              alt=""
              style={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
                objectPosition: "center top",
                filter: "contrast(1.05)",
              }}
            />
            <div
              style={{
                position: "absolute",
                inset: 0,
                background: "linear-gradient(180deg, rgba(52,224,255,0.10), transparent 50%)",
                pointerEvents: "none",
              }}
            />
            <span
              style={{
                position: "absolute",
                bottom: "8px",
                left: "10px",
                fontFamily: "var(--mono)",
                fontSize: "0.62rem",
                letterSpacing: "0.1em",
                color: "var(--cyan)",
              }}
            >
              ◢ LIVE FEED
            </span>
          </>
        )}
      </div>

      <div>
        {projects.map((p, i) => (
          <ProjectRow key={p.name} project={p} num={String(i + 1).padStart(2, "0")} onHover={setPreview} />
        ))}
        <div style={{ borderTop: "1px solid var(--line)" }} />
      </div>

      <style>{`
        @media (max-width: 1040px) {
          .project-preview { display: none !important; }
          .project-mobile-image { display: block !important; }
        }
        @media (max-width: 600px) {
          .project-row-inner {
            grid-template-columns: minmax(0, 1fr) !important;
            padding-left: 10px !important;
            padding-right: 10px !important;
          }
          .project-index, .project-arrow { display: none !important; }
        }
      `}</style>
    </section>
  );
}
